
import { GoogleGenAI, Type } from "@google/genai";
import { Story } from "../types";

const MAX_RETRIES = 5;
const INITIAL_BACKOFF = 2000;

// Votre endpoint Cloud Run pour le TTS Amazigh (Route /synthesize)
const TTS_API_URL = 'https://tts-amazighe-287649284501.europe-west1.run.app/synthesize';

/**
 * Utilitaire pour attendre
 */
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Convertit le texte Tifinagh en caractères latins phonétiques pour le modèle Shilha
 */
const transliterateTifinaghToLatin = (text: string): string => {
  const map: Record<string, string> = {
    'ⴰ': 'a', 'ⴱ': 'b', 'ⴳ': 'g', 'ⴷ': 'd', 'ⴹ': 'd', 'ⴻ': 'e',
    'ⴼ': 'f', 'ⴽ': 'k', 'ⵀ': 'h', 'ⵃ': 'h', 'ⵄ': 'a', 'ⵅ': 'x',
    'ⵇ': 'q', 'ⵉ': 'i', 'ⵊ': 'j', 'ⵍ': 'l', 'ⵎ': 'm', 'ⵏ': 'n',
    'ⵓ': 'u', 'ⵔ': 'r', 'ⵕ': 'r', 'ⵙ': 's', 'ⵚ': 's', 'ⵜ': 't',
    'ⵟ': 't', 'ⵡ': 'w', 'ⵢ': 'y', 'ⵣ': 'z', 'ⵥ': 'z', 'ⵛ': 'sh',
    'ⵯ': 'w', 'ⴲ': 'b', 'ⴴ': 'g', 'ⴵ': 'dj', 'ⴶ': 'dj', 'ⴸ': 'd', 
    'ⴺ': 'd', 'ⴾ': 'k', 'ⴿ': 'kh', 'ⵁ': 'h', 'ⵂ': 'h', 'ⵝ': 't',
    'ⵞ': 'ch', 'ⵠ': 'v', 'ⵤ': 'z', 'ⵧ': 'g'
  };
  
  return text.split('').map(char => map[char] || char).join('');
};

/**
 * Sanitize error message to prevent API key leakage
 */
export const sanitizeError = (message: string): string => {
  const apiKey = process.env.GEMINI_API_KEY || '';
  if (apiKey && message.includes(apiKey)) {
    return message.split(apiKey).join('[HIDDEN_KEY]');
  }
  // Also common patterns for API keys
  return message.replace(/AIza[0-9A-Za-z-_]{35}/g, '[HIDDEN_KEY]');
};

/**
 * Wrapper pour exécuter un appel API avec gestion du retry
 */
async function callWithRetry<T>(fn: () => Promise<T>, retryCount = 0): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    const rawErrorMsg = (error?.message || "");
    const errorMsg = rawErrorMsg.toLowerCase();
    
    // On réessaie en cas d'erreur réseau ou timeout
    const shouldRetry = errorMsg.includes('failed to fetch') || 
                        errorMsg.includes('networkerror') ||
                        errorMsg.includes('timeout') ||
                        errorMsg.includes('abort') ||
                        error?.status === 503 ||
                        error?.status === 504;

    if (shouldRetry && retryCount < MAX_RETRIES) {
      const delay = INITIAL_BACKOFF * Math.pow(1.5, retryCount);
      console.warn(`[Retry] Tentative ${retryCount + 1}/${MAX_RETRIES} après erreur: ${sanitizeError(rawErrorMsg)}`);
      await wait(delay);
      return callWithRetry(fn, retryCount + 1);
    }
    
    // On sanitize l'erreur avant de la relancer
    if (error instanceof Error) {
      error.message = sanitizeError(error.message);
    }
    throw error;
  }
}

/**
 * Génère un conte en Amazigh (Tifinagh)
 */
export const generateStoryContent = async (
  prompt: string, 
  inputLanguage: string, 
  pageCount: number = 4
): Promise<Story> => {
  return callWithRetry(async () => {
    const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY || '' });
    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash', 
      contents: `Write a beautiful children's story based on this idea: "${prompt}". 
      The user input language is ${inputLanguage}.
      
      STRICT LINGUISTIC REQUIREMENTS:
      1. Language: Moroccan Amazigh (Standard Moroccan Tamazight).
      2. Script: MANDATORY use of normalized Tifinagh characters (IRCAM standard).
      3. Length: Exactly ${pageCount} distinct scenes.
      
      Output the result as JSON.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            segments: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  text: { type: Type.STRING },
                  imagePrompt: { type: Type.STRING }
                },
                required: ["text", "imagePrompt"]
              }
            }
          },
          required: ["title", "segments"]
        }
      }
    });

    if (!response.text) throw new Error("EMPTY_TEXT_RESPONSE");
    return JSON.parse(response.text);
  });
};

/**
 * Génère une illustration
 */
export const generateImageForSegment = async (imagePrompt: string): Promise<string> => {
  return callWithRetry(async () => {
    const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY || '' });
    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash',
      contents: {
        parts: [{ text: `High quality children book illustration, vibrant colors, magical atmosphere: ${imagePrompt}` }]
      },
      config: { imageConfig: { aspectRatio: "16:9" } }
    });

    const candidate = response.candidates?.[0];
    if (candidate?.content?.parts) {
      for (const part of candidate.content.parts) {
        if (part.inlineData) return `data:image/png;base64,${part.inlineData.data}`;
      }
    }
    return 'https://picsum.photos/800/450';
  });
};

/**
 * Génère la voix Amazighe en utilisant l'API Cloud Run personnalisée.
 */
export const generateAudioForText = async (text: string, variant: string = 'Tachelhit'): Promise<string> => {
  if (!text || text.trim().length === 0) throw new Error("TEXT_EMPTY");

  return callWithRetry(async () => {
    const latinText = transliterateTifinaghToLatin(text);
    
    // Utilisation d'un AbortController pour gérer un timeout côté client
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 secondes

    try {
      console.debug(`[AudioService] Requête vers ${TTS_API_URL} pour: ${latinText.substring(0, 30)}...`);
      const response = await fetch(TTS_API_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          text: latinText, 
          variant: variant 
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        throw new Error(`Erreur TTS (${response.status}): ${errorText || response.statusText}`);
      }
      
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Échec de la synthèse vocale sur le serveur");
      }
      
      return data.audio_base_64;
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error("Délai d'attente dépassé. Le serveur vocal met trop de temps à répondre (Timeout).");
      }
      if (err.message === 'Failed to fetch') {
        throw new Error("Erreur de connexion (Failed to fetch). Vérifiez si le serveur Cloud Run autorise les appels publics (CORS/Authentication).");
      }
      throw err;
    }
  });
};
